/* eslint react-hooks/exhaustive-deps: "warn" */
import { useCallback, useRef } from 'react';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './SalesProspectingLeadGenerationHero.module.scss';

type Registry = {
  flag: string;
  name: string;
};

const REGISTRIES: Registry[] = [
  { flag: '🇬🇧', name: 'Companies House' },
  { flag: '🇩🇪', name: 'Handelsregister' },
  { flag: '🇫🇷', name: 'INSEE Sirene' },
  { flag: '🇳🇱', name: 'KvK' },
  { flag: '🇮🇹', name: 'Registro Imprese' },
  { flag: '🇧🇪', name: 'KBO / BCE' },
  { flag: '🇪🇸', name: 'Registro Mercantil' },
  { flag: '🇵🇹', name: 'Registo Comercial' },
  { flag: '🇮🇪', name: 'CRO' },
  { flag: '🇱🇺', name: 'RCS Luxembourg' },
  { flag: '🇨🇭', name: 'Zefix' },
  { flag: '🇦🇹', name: 'Firmenbuch' },
  { flag: '🇸🇪', name: 'Bolagsverket' },
  { flag: '🇳🇴', name: 'Brønnøysundregistrene' },
  { flag: '🇩🇰', name: 'CVR' },
  { flag: '🇫🇮', name: 'PRH' },
  { flag: '🇵🇱', name: 'KRS' },
  { flag: '🇨🇿', name: 'Obchodní rejstřík' },
  { flag: '🇷🇴', name: 'ONRC' },
  { flag: '🇺🇸', name: 'SEC EDGAR' },
  { flag: '🇨🇦', name: 'Corporations Canada' },
  { flag: '🇦🇺', name: 'ASIC' },
  { flag: '🇳🇿', name: 'Companies Office' },
  { flag: '🇸🇬', name: 'ACRA' },
  { flag: '🇭🇰', name: 'Companies Registry' },
  { flag: '🇯🇵', name: 'Hōjin Bangō' },
  { flag: '🇮🇳', name: 'MCA India' },
  { flag: '🇿🇦', name: 'CIPC' },
  { flag: '🇲🇽', name: 'Registro Público de Comercio' },
  { flag: '🇧🇷', name: 'Receita Federal' },
];

export const SalesProspectingLeadGenerationHero = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);

  const onPointerMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const node = rootRef.current;

    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();

    node.style.setProperty('--px', `${((event.clientX - rect.left) / rect.width) * 100}%`);
    node.style.setProperty('--py', `${((event.clientY - rect.top) / rect.height) * 100}%`);
  }, []);

  return (
    <div className={styles.root} ref={rootRef} onMouseMove={onPointerMove}>
      <Section background="#0b1120" className={styles.heroSection}>
        <div className={styles.glow} aria-hidden="true" />
        <div className={styles.cursorGlow} aria-hidden="true" />

        <TitleBlock
          size="page"
          theme="dark"
          title={t('Build target lists from 600M+ verified companies')}
          description={t(
            'Every record traces back to a government registry filing. Segment on the industry code a country actually uses, on filed accounts, on incorporation date and officer structure. Every row arrives carrying its registry identifier, so it lands in your CRM without creating duplicates.',
          )}
        />

        <div className={styles.actions}>
          <Button type="blue" size="xxl" href="/free-trial">
            {t('Build a list free')}
          </Button>

          <Button type="white-outline" size="xxl" href="/data-sources">
            {t('View data sources')}
          </Button>
        </div>
      </Section>

      <div className={styles.rail} aria-label={t('Sourced directly from 400+ government registries')}>
        <span className={styles.railLabel}>{t('Sourced directly from 400+ government registries')}</span>

        <div className={styles.railMask}>
          <div className={styles.railTrack}>
            {[...REGISTRIES, ...REGISTRIES].map((registry, index) => (
              <span key={`${registry.name}-${index}`} className={styles.railItem}>
                <span className={styles.railFlag} aria-hidden="true">
                  {registry.flag}
                </span>
                <b>{registry.name}</b>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
